import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { HiChevronLeft, HiChevronRight, HiX } from 'react-icons/hi'
import { getOptimizedImageUrl } from '../lib/utils'

export default function Gallery({ content }) {
  const rawImages = content?.gallery_images || []
  const title = content?.gallery_title || 'גלריה'
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Pre-optimize images
  const images = rawImages.map(url => getOptimizedImageUrl(url, 800, 75))

  if (images.length === 0) return null

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const handleDragEnd = (e, { offset }) => {
    // In RTL viewing, physical swipe left (negative offset) reveals the PREV item.
    // Swipe right (positive offset) reveals the NEXT item.
    if (offset.x < -50) {
      prevSlide()
    } else if (offset.x > 50) {
      nextSlide()
    }
  }

  return (
    <section id="gallery" className="py-24 px-6 bg-[#f5f5f5] w-full flex flex-col items-center overflow-hidden font-rubik" dir="rtl">
      <div className="w-full max-w-[1200px] relative">
        {/* Header */}
        <div className="flex justify-between items-end border-b border-gray-300 pb-3 mb-16 w-full px-4">
          <h2 className="text-3xl md:text-5xl font-light text-gray-800">{title}</h2>
          <span className="text-2xl text-gray-700 font-light tracking-widest" dir="ltr">
            {String(currentIndex + 1).padStart(2, '0')}
            <span className="text-gray-600 text-lg">/{String(images.length).padStart(2, '0')}</span>
          </span>
        </div>

        {/* Carousel Container */}
        <div className="relative w-full h-[400px] md:h-[600px] flex items-center justify-center">
          {images.map((url, i) => {
            const diff = i - currentIndex

            // Normalize diff to support infinite wrap-around visually
            let normalizedDiff = diff
            if (diff > Math.floor(images.length / 2)) normalizedDiff -= images.length
            if (diff < -Math.ceil(images.length / 2)) normalizedDiff += images.length

            const isCenter = normalizedDiff === 0
            const isRight = normalizedDiff === -1 // Physically right in RTL (prev)
            const isLeft = normalizedDiff === 1   // Physically left in RTL (next)

            let x = 0
            let scale = 1
            let opacity = 1
            let zIndex = 30

            if (isCenter) {
              x = 0
              scale = 1
              zIndex = 30
              opacity = 1
            } else if (isRight) {
              x = '65%' // Pushed to the right
              scale = 0.75
              zIndex = 20
              opacity = 0.5
            } else if (isLeft) {
              x = '-65%' // Pushed to the left
              scale = 0.75
              zIndex = 20
              opacity = 0.5
            } else {
              x = normalizedDiff > 0 ? '-100%' : '100%'
              scale = 0.5
              zIndex = 10
              opacity = 0
            }

            return (
              <motion.div
                key={i}
                initial={false}
                animate={{ x, scale, opacity, zIndex }}
                transition={{ type: 'spring', stiffness: 260, damping: 25 }}
                drag={isCenter ? 'x' : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={isCenter ? handleDragEnd : undefined}
                onClick={() => {
                  if (isCenter) setIsFullscreen(true)
                  if (isRight) prevSlide()
                  if (isLeft) nextSlide()
                }}
                className={`absolute w-[80%] sm:w-[70%] md:w-[45%] aspect-square rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-2xl bg-white ${
                  isCenter ? 'cursor-zoom-in' : 'cursor-pointer'
                }`}
              >
                <img
                  src={url}
                  alt={`Gallery Image ${i + 1}`}
                  className="w-full h-full object-cover pointer-events-none"
                  loading="lazy"
                />
                
                {/* Visual filter for background items */}
                {!isCenter && (
                  <div className="absolute inset-0 bg-white/20 backdrop-blur-[2px] pointer-events-none" />
                )}
              </motion.div>
            )
          })}

          {/* Controls - Only show if there's more than 1 image */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevSlide}
                aria-label="התמונה הקודמת"
                className="absolute right-2 md:right-10 z-40 p-3 md:p-4 bg-white/70 hover:bg-white backdrop-blur-md rounded-full shadow-lg text-gray-800 transition-all active:scale-95 border border-white/40"
              >
                <HiChevronRight size={24} />
              </button>

              <button
                onClick={nextSlide}
                aria-label="התמונה הבאה"
                className="absolute left-2 md:left-10 z-40 p-3 md:p-4 bg-white/70 hover:bg-white backdrop-blur-md rounded-full shadow-lg text-gray-800 transition-all active:scale-95 border border-white/40"
              >
                <HiChevronLeft size={24} />
              </button>
            </>
          )}
        </div>

        {/* Pagination Dots */}
        {images.length > 1 && (
          <div className="flex justify-center items-center gap-2 mt-12">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-1.5 transition-all duration-300 rounded-full ${
                  i === currentIndex ? 'w-10 bg-gray-600' : 'w-4 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Fullscreen Lightbox Modal */}
      <AnimatePresence>
        {isFullscreen && images.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md"
            onClick={() => setIsFullscreen(false)}
          >
            <button
              onClick={() => setIsFullscreen(false)}
              className="absolute top-4 right-4 md:top-8 md:right-8 z-[110] p-2 md:p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all"
            >
              <HiX size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={images[currentIndex]}
              alt={`Fullscreen Image`}
              className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl relative z-[100]"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
